import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { PageContent, Spinner, CisContainer, Table } from 'components';
import { connect } from 'react-redux';
import {
  loadIpmaCisCreator,
  setIpmaCisPageCreator,
  setIpmaCisPerPageCreator,
  setIpmaCisSortCreator,
  setIpmaCisFiltersCreator,
  removeIpmaCisFiltersCreator,
} from 'store/actionsCreators';
import PropTypes from 'prop-types';
import { isLoading, globalPropTypes } from 'helpers';
import TableHeaderCell from 'components/Table/TableHeaderCell';
import CisTable from 'components/Table/CisTable';
import CisPagination from 'components/Table/CisPagination';
import { loadGlobalCisSignaturesAttributesCreator } from '../../../../store/actionsCreators';
import { formatDate } from 'helpers/date';

const Signatures = ({
  loading,
  memberAssociations,
  list,
  loadList,
  setPage,
  setPerPage,
  setSort,
  setFilters,
  loadSignatureAttributes,
}) => {
  const { filters, items, page, pages, perPage, sort, totalItems } = list;
  const { t } = useTranslation();

  useEffect(() => {
    setPage(1);
    loadList();
  }, [perPage, sort]);

  useEffect(() => {
    loadSignatureAttributes();
  }, []);

  const handleColumnFilterChange = (key, value) => {
    setFilters({ name: key, value });
    setPage(1);
    loadList();
  };

  const getSortDirection = (key) => {
    if (!sort) return null;
    const [sortKey, sortDir] = sort.split('.');
    return sortKey === key ? sortDir : null;
  };

  const handleColumnSort = (key, direction) => {
    if (direction) {
      setSort(`${key}.${direction}`);
    } else {
      setSort(null);
    }
  };

  return (
    <PageContent>
      <CisContainer>
        <CisTable>
          <CisTable.Head>
            <CisTable.Row>
              <CisTable.StickyHeading>
                <TableHeaderCell label={t('global.id')} />
              </CisTable.StickyHeading>
              <CisTable.StickyHeading>
                <TableHeaderCell
                  label={t('page.user.cis.signatures.memberAssociation')}
                  filter
                  filterType="select"
                  filterOptions={memberAssociations}
                  filterValue={filters.memberAssociation}
                  onFilter={(val) => handleColumnFilterChange('memberAssociation', val)}
                  filterPlaceholder={t('page.user.cis.signatures.memberAssociationPlaceholder')}
                  sort
                  sortDirection={getSortDirection('memberAssociation')}
                  onSort={(dir) => handleColumnSort('memberAssociation', dir)}
                />
              </CisTable.StickyHeading>
              <CisTable.StickyHeading>
                <TableHeaderCell
                  filter
                  filterType="input"
                  filterValue={filters.fullName || ''}
                  onFilter={(val) => handleColumnFilterChange('fullName', val)}
                  filterPlaceholder={t('page.user.cis.signatures.fullNamePlaceholder')}
                  label={t('page.user.cis.signatures.fullName')}
                  sort
                  sortDirection={getSortDirection('fullName')}
                  onSort={(dir) => handleColumnSort('fullName', dir)}
                />
              </CisTable.StickyHeading>
              <CisTable.StickyHeading>
                <TableHeaderCell
                  filter
                  filterType="datepicker-range"
                  filterValueFrom={filters.dateFromFrom || ''}
                  filterValueTo={filters.dateFromTo || ''}
                  onFilterFrom={(val) => handleColumnFilterChange('dateFromFrom', val)}
                  onFilterTo={(val) => handleColumnFilterChange('dateFromTo', val)}
                  filterPlaceholder={t('page.user.cis.signatures.dateFromPlaceholder')}
                  label={t('page.user.cis.signatures.dateFrom')}
                  labelAlign="center"
                  sort
                  sortDirection={getSortDirection('dateFrom')}
                  onSort={(dir) => handleColumnSort('dateFrom', dir)}
                />
              </CisTable.StickyHeading>
              <CisTable.StickyHeading>
                <TableHeaderCell label={t('page.user.cis.signatures.type')} labelAlign="center" />
              </CisTable.StickyHeading>
              <CisTable.StickyHeading>
                <TableHeaderCell
                  label={t('page.user.cis.signatures.signature')}
                  labelAlign="center"
                />
              </CisTable.StickyHeading>
            </CisTable.Row>
          </CisTable.Head>
          <CisTable.Body>
            {loading ? (
              <CisTable.Row>
                <CisTable.Cell colSpan={11}>
                  <Spinner />
                </CisTable.Cell>
              </CisTable.Row>
            ) : null}
            {!loading && !items.length ? (
              <CisTable.Row>
                <CisTable.Cell colSpan={11}>{t('global.noResultsFound')}</CisTable.Cell>
              </CisTable.Row>
            ) : null}
            {!loading && items.length
              ? items.map((item) => (
                  <CisTable.Row key={item.id}>
                    <CisTable.Cell>
                      <Table.Label>{t('global.id')}:</Table.Label>
                      {item.id}
                    </CisTable.Cell>
                    <CisTable.Cell>
                      <Table.Label>{t('page.user.cis.signatures.memberAssociation')}:</Table.Label>
                      {item.memberAssociation?.name || '-'}
                    </CisTable.Cell>
                    <CisTable.Cell>
                      <Table.Label>{t('page.user.cis.signatures.fullName')}:</Table.Label>
                      {[item.user.firstName, item.user.middleName, item.user.lastName].join(' ')}
                    </CisTable.Cell>
                    <CisTable.Cell>
                      <Table.Label>{t('page.user.cis.signatures.dateFrom')}:</Table.Label>
                      {formatDate(item.dateFrom, 'L')}
                    </CisTable.Cell>
                    <CisTable.Cell>
                      <Table.Label>{t('page.user.cis.signatures.type')}:</Table.Label>
                      {item.assessorType === 'H' ? t('page.user.cis.signatures.head') : t('page.user.cis.signatures.assessor')}
                    </CisTable.Cell>
                    <CisTable.Cell>
                      <Table.Label>{t('page.user.cis.signatures.signature')}:</Table.Label>
                      {item.signatureType === 'I' && (
                        <span>{t('page.user.cis.signatures.signatureTypeItalic')}</span>
                      )}
                      {(item.signatureType === 'N' || !item.signatureType) && (
                        <span>{t('page.user.cis.signatures.signatureTypeNone')}</span>
                      )}
                      {item.signatureType === 'H' &&
                        (item.hasFileUrl ? t('global.handwritten') : t('global.noPicture'))}
                    </CisTable.Cell>
                  </CisTable.Row>
                ))
              : null}
          </CisTable.Body>
        </CisTable>
      </CisContainer>
      <CisPagination
        styled
        currentPage={page}
        totalPages={pages}
        totalItems={totalItems}
        perPage={perPage}
        onPageChange={(payload) => {
          setPage(payload);
          loadList();
        }}
        onPerPageChange={({ value }) => setPerPage(value)}
      />
    </PageContent>
  );
};

Signatures.propTypes = {
  loading: PropTypes.bool,
  list: globalPropTypes.ListPropsTypes.isRequired,
  loadList: PropTypes.func.isRequired,
  setPage: PropTypes.func.isRequired,
  setPerPage: PropTypes.func.isRequired,
  setSort: PropTypes.func.isRequired,
  setFilters: PropTypes.func.isRequired,
};

Signatures.defaultProps = {
  loading: false,
};

const mapStateToProps = ({ loading, ipma, globals }) => ({
  loading: isLoading(loading, 'LOAD_IPMA_CIS'),
  list: ipma.cis.signatures,
  memberAssociations: globals.availableMA,
});

const mapDispatchToProps = (dispatch) => ({
  loadList: () => dispatch(loadIpmaCisCreator('signatures')),
  setPage: (payload) => dispatch(setIpmaCisPageCreator('signatures', payload)),
  setPerPage: (payload) => dispatch(setIpmaCisPerPageCreator('signatures', payload)),
  setSort: (payload) => dispatch(setIpmaCisSortCreator('signatures', payload)),
  setFilters: (payload) => dispatch(setIpmaCisFiltersCreator('signatures', payload)),
  removeFilters: () => dispatch(removeIpmaCisFiltersCreator('signatures')),
  loadSignatureAttributes: () => dispatch(loadGlobalCisSignaturesAttributesCreator()),
});

export default connect(mapStateToProps, mapDispatchToProps)(Signatures);
